import { Bot, HardDrive, Loader2, Mail, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { type Provider, ProviderCard } from "./ProviderCard";
import { ProviderDialog } from "./ProviderDialog";

type ProviderType = "storage" | "email" | "ai";

const SECTIONS: { type: ProviderType; title: string; icon: typeof HardDrive; addLabel: string }[] =
  [
    { type: "storage", title: "Storage Providers", icon: HardDrive, addLabel: "Add Storage" },
    { type: "email", title: "Email Providers", icon: Mail, addLabel: "Add Email" },
    { type: "ai", title: "AI Providers", icon: Bot, addLabel: "Add AI" },
  ];

export function ProviderSettings() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [createType, setCreateType] = useState<ProviderType>("storage");

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch("/api/providers", { credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : (data.data ?? []));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  function handleCreate(type: ProviderType) {
    setEditingProvider(null);
    setCreateType(type);
    setDialogOpen(true);
  }

  function handleEdit(provider: Provider) {
    setEditingProvider(provider);
    setDialogOpen(true);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm text-destructive">Failed to load providers.</p>
        <Button variant="outline" size="sm" onClick={fetchProviders}>
          Retry
        </Button>
      </div>
    );
  }

  const grouped = {
    storage: providers.filter((p) => p.type === "storage"),
    email: providers.filter((p) => p.type === "email"),
    ai: providers.filter((p) => p.type === "ai"),
  };

  return (
    <div className="space-y-8">
      {SECTIONS.map((section) => {
        const items = grouped[section.type];
        return (
          <div key={section.type} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <section.icon className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-lg font-semibold">{section.title}</h2>
              </div>
              <Button size="sm" onClick={() => handleCreate(section.type)}>
                <Plus className="mr-1.5 h-4 w-4" />
                {section.addLabel}
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No {section.type} providers configured.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {items.map((provider) => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    onEdit={() => handleEdit(provider)}
                    onDeleted={fetchProviders}
                    onRefresh={fetchProviders}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <ProviderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        provider={editingProvider}
        defaultType={createType}
        onSaved={fetchProviders}
        key={editingProvider?.id ?? createType}
      />
    </div>
  );
}
